/*
 * Copyright (c) 2025 Amazon.com, Inc. or its affiliates. All rights reserved.
 *
 * PROPRIETARY/CONFIDENTIAL. USE IS SUBJECT TO LICENSE TERMS.
 */

import {useBundle} from '@amzn/react-arb-tools';
import {
  useRef,
  type ChangeEventHandler,
  type FormEventHandler,
  type PropsWithChildren,
  useContext,
  createContext,
  useMemo,
  useState,
} from 'react';
import {useErrorDisplay} from 'src/utils/useErrorDisplay';
import {
  useFieldArray,
  type UseFieldArrayRemove,
  type UseFieldArrayReturn,
  type FormState,
  useForm,
  type UseFormReset,
  type UseFormHandleSubmit,
} from 'react-hook-form';
import {HOMEPAGE_MESSAGE_BUNDLE_NAME, DEFAULT_LOCALE} from 'src/constants';
import {compressImage} from './PhotoUtils';
import {
  createPhotoItemSchema,
  type PhotoForm,
  MAXIMUM_PHOTO_COUNT,
  type PhotoItem,
  createPhotoFormSchema,
} from './validation';
import {zodResolver} from '@hookform/resolvers/zod';
import PQueue from 'p-queue';
import {useUploadClient} from 'src/api/UploadClientProvider';
import type {RecommendationResult} from 'src/api/APIResponses';

const DOWNSCALING_INITIAL_SIZE = 2000;
const DOWNSCALING_INTERVAL = 150;
const MAX_IMAGE_SIZE = 1 * 1024 * 1024; // 1 MB
const COMPRESSION_TIMEOUT_MS = 5000;

/**
 * Gets the locale from URL query params, falls back to browser locale,
 * then DEFAULT_LOCALE as last resort.
 */
function getLocale(): string {
  const searchParams = new URLSearchParams(window.location.search);
  const urlLocale = searchParams.get('locale');
  
  if (urlLocale) {
    return urlLocale;
  }
  
  // Fall back to browser locale
  if (navigator.language) {
    return navigator.language;
  }
  
  // Last resort fallback
  return DEFAULT_LOCALE;
}

type PhotoFormController = {
  fields: UseFieldArrayReturn<PhotoForm, 'images'>['fields'];
  formState: FormState<PhotoForm>;
  remove: UseFieldArrayRemove;
  reset: UseFormReset<PhotoForm>;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onSubmit: FormEventHandler<HTMLFormElement>;
  lastSubmissionResult: RecommendationResult | Error | null;
};

const PhotoFormControllerContext = createContext<PhotoFormController | null>(
  null,
);

/**
 * Creates the form state for the photo upload form. Descendent components can
 * access the form state and event handlers using {@link usePhotoFormController}.
 */
export const PhotoFormControllerProvider = ({children}: PropsWithChildren) => {
  const [bundle] = useBundle(HOMEPAGE_MESSAGE_BUNDLE_NAME);
  const uploadClient = useUploadClient();
  const {displayError} = useErrorDisplay();
  const [lastSubmissionResult, setLastSubmissionResult] = useState<
    RecommendationResult | Error | null
  >(null);

  // Form state
  const {
    control,
    formState,
    getValues,
    setValue,
    trigger,
    handleSubmit,
    reset,
  } = useForm<PhotoForm>({
    resolver: zodResolver(createPhotoFormSchema(bundle)),
    mode: 'onSubmit',
    defaultValues: {
      images: [],
      locale: getLocale(),
    },
  });
  const {fields, append, remove} = useFieldArray({
    control,
    name: 'images',
  });

  const photoItemSchema = createPhotoItemSchema(bundle);
  const nextFileId = useRef(0);

  // Files should be compressed one at a time so Pica can optimize its resource
  // usage. Use a queue to enforce this and manually memoize it to ensure it doesn't
  // get recreated on render.
  const compressionTaskQueue = useMemo(() => new PQueue({concurrency: 1}), []);

  // Updates the an item in the images field array, if it still exists
  const updateItem = (item: PhotoItem) => {
    // Unlike React state like fields, getValues always returns the latest state
    // when captured in a closure. This makes this function safe to call even
    // after multiple state changes because it won't see a stale state.
    const images = getValues('images');
    const index = images.findIndex(({id}) => id === item.id);
    if (index >= 0) {
      setValue(`images.${index}`, item);
    }
  };

  /**
   * Creates a compression task for the given photo item.
   * @param item The photo item to compress
   */
  const createCompressionTask = (item: PhotoItem) => async () => {
    console.info(
      `Starting compressImageTask for ${item.inputFile.name}`,
    );

    // Item could have been removed from the form by the time this executes
    const images = getValues('images');
    const isItemInForm = images.findIndex(({id}) => item.id === id) >= 0;
    if (!isItemInForm) {
      console.info(
        `Skipping compression for ${item.inputFile.name} because it is no longer in the form`,
      );
      return;
    }

    // Invalid files should not be compressed.
    if (!photoItemSchema.safeParse(item).success) {
      console.info(
        `Skipping compression for ${item.inputFile.name} because it is invalid`,
      );
      updateItem({
        ...item,
        compressionStatus: 'failed',
      });
      return;
    }

    try {
      // We have a 6MB limit on total request size because we are uploading files
      // to a Lambda, which imposes a request size limit. Request headers take up
      // space so let's assume we can upload 4MB of photos to leave some wiggle
      // room. For four photos, that leaves us with 1MB per-photo. To get each
      // photo under 1MB, we apply increasingly greater downscaling and
      // compression to the image. We start by downscaling to a maximum dimension
      // of 2000 pixels, then decrease that by 150 pixels each iteration until
      // we reach the target size. The minimum image dimension we will downscale
      // to is 650 pixels.
      const maxCompressionAttempts = 10;
      // Always process the original image to avoid repeated application of
      // compression quality settings.
      const fileToCompress = item.inputFile;
      let smallestFile = item.inputFile;
      for (let i = 0; i < maxCompressionAttempts; i++) {
        const compressedFile = await compressImage(fileToCompress, {
          max: DOWNSCALING_INITIAL_SIZE - DOWNSCALING_INTERVAL * i,
          signal: AbortSignal.timeout(COMPRESSION_TIMEOUT_MS),
        });
        smallestFile =
          compressedFile.size < smallestFile.size
            ? compressedFile
            : smallestFile;

        console.info(
          `Compressed ${fileToCompress.name} from ${fileToCompress.size} bytes to ${compressedFile.size} bytes. Smallest file size is ${smallestFile.size}.`,
        );
        updateItem({
          ...item,
          compressedFile: smallestFile,
          compressionStatus: 'success',
        });

        if (smallestFile.size <= MAX_IMAGE_SIZE) {
          console.debug(
            `File size ${smallestFile.size} is less than ${MAX_IMAGE_SIZE}, stopping compression`,
          );
          break;
        }
      }
    } catch (e) {
      console.error('Failed to compress image: ', e);
      displayError(e as Error, `Image compression for ${item.inputFile.name}`);
      updateItem({
        ...item,
        compressionStatus: 'failed',
      });
    }
  };

  const onChange: ChangeEventHandler<HTMLInputElement> = ({
    target: {files},
  }) => {
    const filesArray = Array.from(files ?? []);
    const filesToAddCount = Math.min(
      filesArray.length,
      MAXIMUM_PHOTO_COUNT - fields.length,
    );

    console.info(`Selecting ${filesToAddCount} files from the onChange event`);

    if (filesToAddCount > 0) {
      const photoItems: PhotoItem[] = filesArray
        .slice(0, filesToAddCount)
        .map((file) => {
          const id = nextFileId.current++;
          return {id, inputFile: file, compressionStatus: 'pending'};
        });

      // Add files to form
      append(photoItems);
      console.debug(`added ${photoItems.length} photos to form`);

      // Queue up compression tasks for this batch of files
      const compressionTasks = photoItems.map((photoItem) =>
        createCompressionTask(photoItem),
      );
      compressionTaskQueue.addAll(compressionTasks);
      console.debug(
        `added ${compressionTasks.length} compression tasks to queue`,
      );
    }
  };

  const onSubmit: ReturnType<UseFormHandleSubmit<PhotoForm>> = async (
    event,
  ) => {
    const submitFn = handleSubmit(async (_validatedForm) => {
      console.info('onSubmit handler called');
      console.info(
        `Waiting for ${compressionTaskQueue.size} compression tasks to finish`,
      );
      await compressionTaskQueue.onIdle();

      const isFormValid = await trigger('images');
      if (!isFormValid) {
        throw new Error(
          'Form is invalid after waiting for compression tasks to finish',
        );
      }

      // After all compression tasks are finished, the form may have been
      // mutated. This means we cannot use the _validatedForm parameter. We
      // must refetch the latest form data using getValues so that we don't pass
      // stale data to our upload function.
      const processedForm = getValues();
      console.info(`submitting ${processedForm.images.length} images`);
      const result = await uploadClient.uploadPhotoForm(processedForm);
      setLastSubmissionResult(result);
    });

    try {
      await submitFn(event);
    } catch (e) {
      console.error('Error submitting form', e);
      const error = e as Error;
      setLastSubmissionResult(error);
      displayError(error, 'Form submission');
    }
  };

  // Enhanced reset that also clears submission results
  const resetWithErrorClear = () => {
    reset();
    setLastSubmissionResult(null);
  };

  return (
    <PhotoFormControllerContext.Provider
      value={{
        fields,
        formState,
        remove,
        reset: resetWithErrorClear,
        onChange,
        onSubmit,
        lastSubmissionResult,
      }}>
      {children}
    </PhotoFormControllerContext.Provider>
  );
};

export function usePhotoFormController(): PhotoFormController {
  const controller = useContext(PhotoFormControllerContext);
  if (!controller) {
    throw new Error(
      'usePhotoFormController must be called in a descendent of an PhotoFormControllerProvider component.',
    );
  }

  return controller;
}
